/**
 * BureaucraticMachine — Cordialement.ai signature illustration.
 *
 * Editorial SVG: raw angry note → absurd administrative device → sendable letter.
 * Uses the brand palette (black, cream, mint) + a single red accent for the raw message.
 * Transparent background, scalable, screen-reader friendly.
 */
export const BureaucraticMachine = ({ className = "" }) => (
  <svg
    viewBox="0 0 480 640"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label="Machine bureaucratique Cordialement.ai : transforme un message brut en réponse professionnelle."
    fill="none"
    stroke="#0A0A0A"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* ==== 1. RAW PAPER (top, tilted, aggressive red text) ==== */}
    <g className="paper-float" style={{ ["--rot"]: "-4deg" }}>
      <g transform="translate(70 30) rotate(-4)">
        {/* paper with crumpled corner */}
        <path
          d="M0 12 L20 0 L280 0 L300 14 L292 168 L272 176 L18 172 L0 160 Z"
          fill="#FFFFFF"
        />
        {/* fold shadow */}
        <path d="M280 0 L300 14 L288 22 Z" fill="#F3EFE7" />
        {/* header stripe */}
        <line x1="20" y1="30" x2="278" y2="30" strokeWidth="1.5" opacity="0.4" />
        {/* "MESSAGE INITIAL · CONFIDENTIEL" label */}
        <text
          x="22"
          y="22"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="9"
          fontWeight="700"
          letterSpacing="1.4"
          fill="#4A4A4A"
        >
          MESSAGE INITIAL · CONFIDENTIEL
        </text>
        {/* angry red text — the "what you think" moment */}
        <text
          x="24"
          y="72"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="18"
          fontWeight="900"
          letterSpacing="-0.3"
          fill="#D64545"
        >
          MAIS TU TE
        </text>
        <text
          x="24"
          y="98"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="18"
          fontWeight="900"
          letterSpacing="-0.3"
          fill="#D64545"
        >
          F
          <tspan letterSpacing="-1">██</tspan>
          {" "}DE MOI ?
        </text>
        {/* scratchy annotation */}
        <path
          d="M24 118 Q60 114 96 118 T172 118"
          stroke="#D64545"
          strokeWidth="2"
          fill="none"
          opacity="0.7"
        />
        {/* signature squiggle */}
        <path
          d="M180 148 q10 -8 20 0 t20 0 t20 0"
          stroke="#4A4A4A"
          strokeWidth="1.5"
          fill="none"
        />
      </g>
    </g>

    {/* ==== 2. DOWN ARROW → INTO THE MACHINE ==== */}
    <g transform="translate(240 218)">
      <line x1="0" y1="0" x2="0" y2="38" strokeWidth="2.5" />
      <polyline points="-7,32 0,40 7,32" fill="none" strokeWidth="2.5" />
    </g>

    {/* ==== 3. THE MACHINE (center) ==== */}
    <g transform="translate(60 268)">
      {/* body with nb-shadow */}
      <rect x="4" y="4" width="360" height="130" fill="#0A0A0A" />
      <rect x="0" y="0" width="360" height="130" fill="#FBF9F6" stroke="#0A0A0A" strokeWidth="2.5" />
      {/* brand strip */}
      <rect x="0" y="0" width="360" height="24" fill="#0A0A0A" />
      <text
        x="14"
        y="16"
        fontFamily="IBM Plex Sans, sans-serif"
        fontSize="10"
        fontWeight="800"
        letterSpacing="2.4"
        fill="#FBF9F6"
      >
        CORDIALEMENT.AI · UNITÉ DE CIVILISATION
      </text>
      <text
        x="346"
        y="16"
        textAnchor="end"
        fontFamily="IBM Plex Sans, sans-serif"
        fontSize="9"
        fontWeight="700"
        letterSpacing="1.6"
        fill="#B5EAD7"
      >
        CDLT-2026
      </text>

      {/* left dial: TON */}
      <g transform="translate(58 78)">
        <circle cx="0" cy="0" r="30" fill="#FBF9F6" stroke="#0A0A0A" strokeWidth="2.5" />
        <circle cx="0" cy="0" r="24" fill="none" stroke="#0A0A0A" strokeWidth="1" opacity="0.3" />
        {/* tick marks */}
        {[-60, -30, 0, 30, 60].map((a) => (
          <line
            key={a}
            x1={22 * Math.cos((a - 90) * Math.PI / 180)}
            y1={22 * Math.sin((a - 90) * Math.PI / 180)}
            x2={27 * Math.cos((a - 90) * Math.PI / 180)}
            y2={27 * Math.sin((a - 90) * Math.PI / 180)}
            strokeWidth="1.5"
          />
        ))}
        {/* needle (animated) */}
        <g className="machine-needle">
          <line x1="0" y1="0" x2="0" y2="-22" strokeWidth="2.5" stroke="#D64545" />
        </g>
        <circle cx="0" cy="0" r="3" fill="#0A0A0A" />
        <text
          x="0"
          y="46"
          textAnchor="middle"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="9"
          fontWeight="800"
          letterSpacing="1.6"
          fill="#0A0A0A"
        >
          TON
        </text>
      </g>

      {/* right dial: DIPLOMATIE */}
      <g transform="translate(180 78)">
        <circle cx="0" cy="0" r="30" fill="#B5EAD7" stroke="#0A0A0A" strokeWidth="2.5" />
        <circle cx="0" cy="0" r="24" fill="none" stroke="#0A0A0A" strokeWidth="1" opacity="0.4" />
        {[-60, -30, 0, 30, 60].map((a) => (
          <line
            key={a}
            x1={22 * Math.cos((a - 90) * Math.PI / 180)}
            y1={22 * Math.sin((a - 90) * Math.PI / 180)}
            x2={27 * Math.cos((a - 90) * Math.PI / 180)}
            y2={27 * Math.sin((a - 90) * Math.PI / 180)}
            strokeWidth="1.5"
          />
        ))}
        <line x1="0" y1="0" x2="17" y2="-14" strokeWidth="2.5" />
        <circle cx="0" cy="0" r="3" fill="#0A0A0A" />
        <text
          x="0"
          y="46"
          textAnchor="middle"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="9"
          fontWeight="800"
          letterSpacing="1.4"
          fill="#0A0A0A"
        >
          DIPLOMATIE
        </text>
      </g>

      {/* pilot LEDs / status */}
      <g transform="translate(260 62)">
        <circle cx="0" cy="0" r="4" fill="#B5EAD7" stroke="#0A0A0A" strokeWidth="1.5" />
        <circle cx="0" cy="20" r="4" fill="#FBF9F6" stroke="#0A0A0A" strokeWidth="1.5" />
        <circle cx="0" cy="40" r="4" fill="#FBF9F6" stroke="#0A0A0A" strokeWidth="1.5" />
        <text
          x="12"
          y="3"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="8"
          fontWeight="700"
          letterSpacing="1.2"
          fill="#0A0A0A"
        >
          EN COURS
        </text>
        <text
          x="12"
          y="23"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="8"
          fontWeight="600"
          letterSpacing="1.2"
          fill="#4A4A4A"
        >
          RELECTURE
        </text>
        <text
          x="12"
          y="43"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="8"
          fontWeight="600"
          letterSpacing="1.2"
          fill="#4A4A4A"
        >
          ENVOI
        </text>
      </g>

      {/* input funnel top */}
      <path
        d="M170 -12 L170 0 L190 0 L190 -12 Z"
        fill="#FBF9F6"
        stroke="#0A0A0A"
        strokeWidth="2.5"
      />
      {/* output chute bottom */}
      <path
        d="M170 130 L170 142 L190 142 L190 130 Z"
        fill="#FBF9F6"
        stroke="#0A0A0A"
        strokeWidth="2.5"
      />
    </g>

    {/* ==== 4. DOWN ARROW → OUT ==== */}
    <g transform="translate(240 418)">
      <line x1="0" y1="0" x2="0" y2="30" strokeWidth="2.5" />
      <polyline points="-7,24 0,32 7,24" fill="none" strokeWidth="2.5" />
    </g>

    {/* ==== 5. CLEAN OUTPUT PAPER (bottom, mint stamp) ==== */}
    <g className="paper-float" style={{ ["--rot"]: "2.5deg", animationDelay: "1.2s" }}>
      <g transform="translate(80 460) rotate(2.5)">
        {/* clean paper */}
        <rect x="0" y="0" width="300" height="160" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="2.5" />
        {/* mint accent strip left */}
        <rect x="0" y="0" width="6" height="160" fill="#B5EAD7" />
        {/* header */}
        <text
          x="18"
          y="20"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="9"
          fontWeight="700"
          letterSpacing="1.4"
          fill="#4A4A4A"
        >
          VERSION ENVOYABLE · DOSSIER CDLT-2026
        </text>
        <line x1="18" y1="28" x2="282" y2="28" strokeWidth="1" opacity="0.3" />
        {/* civilized text */}
        <text
          x="18"
          y="50"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="11"
          fontWeight="500"
          fill="#0A0A0A"
        >
          Merci pour ton retour.
        </text>
        <text
          x="18"
          y="68"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="11"
          fontWeight="500"
          fill="#0A0A0A"
        >
          Je pense néanmoins qu'il serait
        </text>
        <text
          x="18"
          y="86"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="11"
          fontWeight="500"
          fill="#0A0A0A"
        >
          utile de reconsidérer ce point.
        </text>
        <text
          x="18"
          y="118"
          fontFamily="IBM Plex Sans, sans-serif"
          fontSize="11"
          fontWeight="700"
          fill="#0A0A0A"
          fontStyle="italic"
        >
          Bien cordialement,
        </text>

        {/* mint stamp — rotated */}
        <g transform="translate(202 128) rotate(-8)">
          <rect
            x="-46"
            y="-14"
            width="92"
            height="28"
            fill="#B5EAD7"
            stroke="#0A0A0A"
            strokeWidth="2"
          />
          <text
            x="0"
            y="4"
            textAnchor="middle"
            fontFamily="IBM Plex Sans, sans-serif"
            fontSize="8"
            fontWeight="800"
            letterSpacing="1.4"
            fill="#0A0A0A"
          >
            SOCIALEMENT
          </text>
          <text
            x="0"
            y="14"
            textAnchor="middle"
            fontFamily="IBM Plex Sans, sans-serif"
            fontSize="7"
            fontWeight="700"
            letterSpacing="1.2"
            fill="#0A0A0A"
          >
            ACCEPTABLE
          </text>
        </g>
      </g>
    </g>
  </svg>
);
